/**
 * ToolsDiscountPage — one-time $97 All-Access offer after RV Match application
 * submit. Everything included: all 4 buying tools + Daily Matches + VIP Buyer
 * Concierge. 2-minute countdown; if the user skips OR the timer expires, they
 * continue to /my-matches/:sessionId and regular pricing applies.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Calculator, FileSearch, MessageSquareText, Check, Clock, Loader2, ChevronRight, X,
  ShieldCheck, BadgeDollarSign, Sparkles, Crown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTheme } from '../context/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WINDOW_SECONDS = 2 * 60; // 2 minutes

const TOOLS = [
  {
    id: 'cost-calculator',
    icon: Calculator,
    title: 'RV Cost Calculator',
    tagline: 'See the TRUE 5-year cost of any RV — beyond the sticker price.',
    image: '/tool-images/cost-calculator.png',
    images: ['/tool-images/cost-calculator.png', '/tool-images/cost-calculator-2.png', '/tool-images/cost-calculator-3.png'],
    accent: 'emerald',
    accentClasses: {
      icon: 'bg-emerald-500/15 text-emerald-500',
      ring: 'ring-emerald-500/20',
      border: 'border-emerald-500/20',
    },
    description:
      'The dealership only shows you the monthly payment. The Cost Calculator shows you everything else: depreciation, fuel, insurance, storage, maintenance, campground fees, registration, and financing interest — year by year.',
    features: [
      'Total 5-year cost of ownership',
      'Year-by-year cost breakdown chart',
      'Insurance & financing estimates included',
      'Compare RV models side-by-side',
    ],
    sample: {
      label: 'Real example',
      lines: [
        ['Sticker price', '$45,000'],
        ['Financing interest (60mo @ 7.5%)', '+ $9,200'],
        ['Insurance & registration', '+ $4,800'],
        ['Fuel + campground fees', '+ $7,100'],
        ['Maintenance + storage', '+ $5,400'],
        ['TRUE 5-year cost', '$71,500'],
      ],
      footnote: "Avg monthly: $1,192 — that's 60% more than the loan payment alone.",
    },
  },
  {
    id: 'deal-analyzer',
    icon: FileSearch,
    title: 'RV Deal Analyzer',
    tagline: 'Stop guessing. Know in 30 seconds whether the price is fair, a steal, or a rip-off.',
    image: '/tool-images/deal-analyzer.png',
    images: ['/tool-images/deal-analyzer.png', '/tool-images/deal-analyzer-2.png', '/tool-images/deal-analyzer-3.png'],
    accent: 'sky',
    accentClasses: {
      icon: 'bg-sky-500/15 text-sky-500',
      ring: 'ring-sky-500/20',
      border: 'border-sky-500/20',
    },
    description:
      "Paste a listing or photo of the dealer's offer. The AI compares it to thousands of similar units across the country and tells you whether you're overpaying — and by how much. You walk into the dealership knowing your number.",
    features: [
      'AI compares your listing to comparable national listings',
      'Steal / Fair / Overpriced verdict with reasons',
      'Counter-offer suggestion + walk-away price',
      'Catches hidden fees & dealer add-on traps',
    ],
    sample: {
      label: 'Real example',
      lines: [
        ['Listed price', '$58,995'],
        ['Market average (similar unit)', '$51,200'],
        ['Verdict', 'OVERPRICED'],
        ['Suggested counter-offer', '$49,800'],
        ['Walk-away price', '$53,500'],
      ],
      footnote: 'You could save up to $9,195 by negotiating with these numbers.',
    },
  },
  {
    id: 'negotiation-coach',
    icon: MessageSquareText,
    title: 'RV Negotiation Coach',
    tagline: "The exact words to say to lock in a fair deal — even if you've never negotiated before.",
    image: '/tool-images/negotiation-coach.png',
    images: ['/tool-images/negotiation-coach.png', '/tool-images/negotiation-coach-2.png', '/tool-images/negotiation-coach-3.png'],
    accent: 'rose',
    accentClasses: {
      icon: 'bg-rose-500/15 text-rose-500',
      ring: 'ring-rose-500/20',
      border: 'border-rose-500/20',
    },
    description:
      "Most buyers lose thousands at the dealership because they don't know what to say. The Negotiation Coach gives you the opening line, the counter-offer math, the comebacks for every dealer pushback, and a walk-away threshold. Like having a former RV sales manager whispering in your ear.",
    features: [
      'Word-for-word opening line for the dealer',
      'Counter-offer math & justification',
      'Comebacks for the 8 most common pushbacks',
      'Walk-away signals so you never overpay',
    ],
    sample: {
      label: 'Real example',
      lines: [
        ['Your line', "\"I've seen this exact unit at $51,200 — what can we do today?\""],
        ['If they say "we can\'t go that low"', '"What can you do? I\'m ready to sign right now at $52,500 OTD."'],
        ['If they push warranty/add-ons', '"Show me the line items. I\'ll consider service contract, skip the rest."'],
      ],
      footnote: 'Buyers using this script save an average of $4,300 on their deal.',
    },
  },
];

const ToolsDiscountPage = () => {
  const { sessionId } = useParams();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [purchasing, setPurchasing] = useState(false);

  // Persist start time so refresh / re-entry doesn't reset the timer
  const startedAtKey = `rv_discount_start_${sessionId}`;
  const startedAt = useMemo(() => {
    const existing = localStorage.getItem(startedAtKey);
    if (existing) {
      const n = parseInt(existing, 10);
      if (!Number.isNaN(n)) return n;
    }
    const now = Date.now();
    localStorage.setItem(startedAtKey, String(now));
    return now;
  }, [startedAtKey]);

  const [secondsLeft, setSecondsLeft] = useState(() => {
    const elapsed = Math.floor((Date.now() - startedAt) / 1000);
    return Math.max(0, WINDOW_SECONDS - elapsed);
  });

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      setSecondsLeft(Math.max(0, WINDOW_SECONDS - elapsed));
    }, 1000);
    return () => clearInterval(t);
  }, [secondsLeft, startedAt]);

  // Auto-advance to matches once the window expires. Forwards ?welcome=1
  // through so the matches dashboard fires the celebratory reveal.
  const welcomeFlag = new URLSearchParams(window.location.search).get('welcome') === '1' ? '?welcome=1' : '';
  useEffect(() => {
    if (secondsLeft <= 0 && sessionId) {
      const t = setTimeout(() => navigate(`/my-matches/${sessionId}${welcomeFlag}`), 800);
      return () => clearTimeout(t);
    }
  }, [secondsLeft, sessionId, navigate, welcomeFlag]);

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const ss = String(secondsLeft % 60).padStart(2, '0');
  const expired = secondsLeft <= 0;

  const handleBuy = async () => {
    if (expired || purchasing) return;
    setPurchasing(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        product_id: 'all_access',
        origin_url: window.location.origin,
        lead_session_id: sessionId,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        throw new Error('No checkout URL');
      }
    } catch (e) {
      console.error(e);
      setPurchasing(false);
      alert('Could not start checkout. Please try again or skip to your matches.');
    }
  };

  const goToMatches = () => navigate(`/my-matches/${sessionId}${welcomeFlag}`);

  return (
    <div
      className={`min-h-screen ${isDark ? 'bg-gray-950' : 'bg-gradient-to-b from-[#004E86] via-[#0a4d7a] to-gray-50'} pb-16`}
      data-testid="tools-discount-page"
    >
      {/* Top utility bar */}
      <div className={`sticky top-0 z-40 backdrop-blur-md ${isDark ? 'bg-gray-900/80' : 'bg-white/85'} border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <span
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold tracking-wide ${
              expired
                ? 'bg-red-500/15 text-red-400 ring-1 ring-red-500/30'
                : 'bg-[#F2A900]/20 text-[#b07700] ring-1 ring-[#F2A900]/40'
            }`}
            data-testid="discount-countdown"
          >
            <Clock className="w-3.5 h-3.5" />
            {expired ? 'Offer expired' : `One-time offer ends in ${mm}:${ss}`}
          </span>
          <button
            onClick={goToMatches}
            className={`inline-flex items-center gap-1.5 text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
            data-testid="skip-to-matches"
          >
            Skip — see my matches <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-10">
        {/* Hero — the FREE matches are the star; the bundle is optional */}
        <div className="text-center text-white mb-8">
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight mb-3 drop-shadow">
            Your RV matches are ready!
          </h1>
          <p className="text-base sm:text-lg max-w-2xl mx-auto text-blue-100 drop-shadow mb-6">
            Your personalized matches are waiting on your dashboard — completely free, no purchase needed.
          </p>
          <Button
            onClick={goToMatches}
            className="w-full sm:w-auto sm:px-20 bg-white hover:bg-blue-50 text-[#004E86] font-extrabold py-7 text-xl rounded-2xl shadow-xl"
            data-testid="continue-to-matches-primary"
          >
            See My Free Matches <ChevronRight className="w-6 h-6 ml-1" />
          </Button>
          <p className="text-xs text-blue-200/80 mt-3">
            Optional: finishing your application unlocked a one-time All-Access offer below — it disappears when the timer ends.
          </p>
        </div>

        {/* All-Access offer card — one single option: everything for $97 */}
        <div className={`mx-auto max-w-lg rounded-2xl ${isDark ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-gray-200'} shadow-lg p-5 sm:p-6 mb-10`}>
          <div className="text-center">
            <p className={`text-xs font-bold uppercase tracking-widest mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              One-time All-Access offer
            </p>
            <p className={`text-xs font-bold uppercase tracking-widest mb-2 ${expired ? 'text-gray-500' : 'text-[#F2A900]'}`}>
              {expired ? 'This offer has expired' : 'Everything we make — one payment'}
            </p>
            <div className="flex items-baseline gap-2 justify-center">
              <span
                className={`text-3xl sm:text-4xl font-extrabold ${isDark ? 'text-white' : 'text-gray-900'}`}
                data-testid="discount-price"
              >
                $97
              </span>
              <span className={`text-lg line-through font-semibold ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>$500+ value</span>
            </div>
            {!expired && (
              <p className="text-sm font-semibold text-emerald-500 mt-1">Only available right now — never offered again</p>
            )}
          </div>

          {/* Everything included */}
          <ul className={`mt-5 space-y-2 text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`} data-testid="all-access-included">
            {[
              ['All 4 RV Buying Tools', 'Cost Calculator, RV vs RV Comparison, Deal Analyzer, Negotiation Coach — lifetime access'],
              ['RV Daily Matches', 'Fresh personalized matches delivered daily — lifetime access'],
              ['VIP Buyer Concierge', '30 days of hands-on help: deeper RV search, review of up to 3 sale offers, trade guidance, negotiation prep, up to 3 calls'],
            ].map(([title, detail]) => (
              <li key={title} className="flex items-start gap-2.5">
                <Crown className="w-4 h-4 mt-0.5 shrink-0 text-[#F2A900]" />
                <span><strong>{title}</strong> — <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{detail}</span></span>
              </li>
            ))}
          </ul>

          {/* CTA */}
          <div className="mt-6">
            {expired ? (
              <Link to={`/my-matches/${sessionId}`}>
                <Button
                  className="w-full bg-[#004E86] hover:bg-[#003d6b] text-white font-bold py-6 text-lg rounded-2xl"
                  data-testid="discount-expired-cta"
                >
                  See my RV matches <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </Link>
            ) : (
              <Button
                onClick={handleBuy}
                disabled={purchasing}
                className="w-full bg-[#F2A900] hover:bg-[#E89400] text-black font-bold py-4 text-base rounded-xl disabled:opacity-60"
                data-testid="discount-buy-cta"
              >
                {purchasing ? (
                  <span className="inline-flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Redirecting to checkout…</span>
                ) : (
                  <span className="inline-flex items-center gap-2"><Crown className="w-5 h-5" /> Get Everything — $97 One-Time</span>
                )}
              </Button>
            )}
          </div>

          {/* Trust badges */}
          <div className={`mt-5 grid grid-cols-3 gap-2 text-center text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <div className="inline-flex items-center justify-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> Secure Stripe checkout</div>
            <div className="inline-flex items-center justify-center gap-1.5"><BadgeDollarSign className="w-3.5 h-3.5 text-emerald-500" /> Instant delivery</div>
            <div className="inline-flex items-center justify-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[#F2A900]" /> Lifetime access</div>
          </div>
        </div>

        {/* Per-tool product cards */}
        <div className="space-y-12 sm:space-y-16">
          {TOOLS.map((tool, idx) => {
            const Icon = tool.icon;
            const reversed = idx % 2 === 1;
            return (
              <div
                key={tool.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center ${isDark ? 'text-white' : 'text-gray-900'}`}
                data-testid={`product-card-${tool.id}`}
              >
                {/* Image carousel — auto-rotates every 3.5s */}
                <div className={`${reversed ? 'lg:order-2' : ''}`}>
                  <ProductImageCarousel
                    images={tool.images || [tool.image]}
                    altPrefix={tool.title}
                    ringClass={tool.accentClasses.ring}
                    isDark={isDark}
                    testId={tool.id}
                  />
                </div>

                {/* Detail */}
                <div className={`${reversed ? 'lg:order-1' : ''}`}>
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${tool.accentClasses.icon} text-xs font-bold uppercase tracking-wide mb-4`}>
                    <Icon className="w-3.5 h-3.5" />
                    Tool {idx + 1} of 3
                  </div>
                  <h2 className={`text-2xl sm:text-3xl font-extrabold mb-3 drop-shadow ${isDark ? 'text-white' : 'text-white lg:text-gray-900'}`}>
                    {tool.title}
                  </h2>
                  <p className={`text-base mb-5 ${isDark ? 'text-gray-300' : 'text-blue-100 lg:text-gray-700'}`}>
                    {tool.tagline}
                  </p>
                  <p className={`text-sm leading-relaxed mb-5 ${isDark ? 'text-gray-300' : 'text-blue-100/90 lg:text-gray-700'}`}>
                    {tool.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-2 mb-5">
                    {tool.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <Check className={`w-4 h-4 mt-0.5 shrink-0 ${
                          tool.accent === 'emerald' ? 'text-emerald-400' :
                          tool.accent === 'sky' ? 'text-sky-400' : 'text-rose-400'
                        }`} />
                        <span className={`text-sm ${isDark ? 'text-gray-200' : 'text-white lg:text-gray-800'}`}>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Sample */}
                  <div className={`rounded-2xl p-5 ${isDark ? 'bg-gray-900/80 border border-gray-800' : 'bg-white/95 border border-gray-200'} backdrop-blur`}>
                    <p className={`text-xs font-bold uppercase tracking-widest mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tool.sample.label}</p>
                    <div className="space-y-1.5">
                      {tool.sample.lines.map(([k, v], i) => {
                        const isLast = i === tool.sample.lines.length - 1;
                        return (
                          <div
                            key={i}
                            className={`flex items-start justify-between gap-3 ${
                              isLast ? `pt-2 mt-1 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}` : ''
                            }`}
                          >
                            <span className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{k}</span>
                            <span className={`text-xs sm:text-sm font-semibold text-right ${isLast ? (isDark ? 'text-white' : 'text-gray-900') : (isDark ? 'text-gray-200' : 'text-gray-800')}`}>{v}</span>
                          </div>
                        );
                      })}
                    </div>
                    {tool.sample.footnote && (
                      <p className={`mt-3 text-xs italic ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tool.sample.footnote}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className={`mt-14 rounded-3xl ${isDark ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-gray-200'} shadow-2xl p-6 sm:p-8 text-center`}>
          <p className={`text-xs font-bold uppercase tracking-widest mb-2 ${expired ? 'text-gray-500' : 'text-[#F2A900]'}`}>
            {expired ? 'This one-time offer has expired' : 'One-time offer — expires when the timer ends'}
          </p>
          <h3 className={`text-2xl sm:text-3xl font-extrabold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {expired
              ? 'Your free matches are waiting'
              : (<>Everything — all tools + Daily Matches + VIP Concierge — <span className="text-emerald-500">$97</span> <span className="text-base font-semibold line-through text-gray-400">$500+ value</span></>)}
          </h3>
          <p className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            One payment. Lifetime tool access + 30 days of hands-on concierge support. Nothing recurring.
          </p>
          {expired ? (
            <Link to={`/my-matches/${sessionId}`}>
              <Button className="bg-[#004E86] hover:bg-[#003d6b] text-white font-bold px-8 py-6 rounded-2xl text-base">
                See my RV matches <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </Link>
          ) : (
            <Button
              onClick={handleBuy}
              disabled={purchasing}
              className="bg-[#F2A900] hover:bg-[#E89400] text-black font-extrabold px-8 py-6 rounded-2xl text-base disabled:opacity-60"
              data-testid="discount-buy-cta-bottom"
            >
              {purchasing ? (
                <span className="inline-flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Redirecting…</span>
              ) : (
                <span className="inline-flex items-center gap-2"><Crown className="w-5 h-5" /> Claim All-Access — $97</span>
              )}
            </Button>
          )}
          <button
            onClick={goToMatches}
            className={`block mx-auto mt-3 inline-flex items-center justify-center gap-1.5 text-sm py-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid="skip-bottom"
          >
            <X className="w-4 h-4" /> No thanks, just show me my matches
          </button>
        </div>
      </div>
    </div>
  );
};

export default ToolsDiscountPage;

const ProductImageCarousel = ({ images, altPrefix, ringClass, isDark, testId }) => {
  const [active, setActive] = React.useState(0);
  const list = images && images.length > 0 ? images : [];

  React.useEffect(() => {
    if (list.length <= 1) return;
    const t = setInterval(() => setActive(i => (i + 1) % list.length), 3500);
    return () => clearInterval(t);
  }, [list.length]);

  if (list.length === 0) return null;

  return (
    <div
      className={`rounded-2xl overflow-hidden ring-1 ${ringClass} ${isDark ? 'bg-gray-900' : 'bg-white'} shadow-2xl`}
      data-testid={`product-carousel-${testId}`}
    >
      <div className="relative w-full aspect-[4/3]">
        {list.map((src, idx) => (
          <img
            key={src}
            src={src}
            alt={`${altPrefix} screenshot ${idx + 1}`}
            loading={idx === 0 ? 'eager' : 'lazy'}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
              idx === active ? 'opacity-100' : 'opacity-0'
            }`}
            data-testid={idx === 0 ? `product-image-${testId}` : `product-image-${testId}-${idx}`}
          />
        ))}
      </div>
      {/* Dots */}
      {list.length > 1 && (
        <div className={`flex items-center justify-center gap-1.5 py-3 ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
          {list.map((_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setActive(idx)}
              className={`h-1.5 rounded-full transition-all ${
                idx === active ? 'w-6 bg-[#F2A900]' : `w-1.5 ${isDark ? 'bg-gray-700' : 'bg-gray-300'}`
              }`}
              aria-label={`Show screen ${idx + 1}`}
              data-testid={`carousel-dot-${testId}-${idx}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
